import React from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, ArrowRight, Phone } from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { VAN_PRICING, FAQS, COMPANY } from '../mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

const Prices = () => {
  return (
    <>
      <PageHeader
        crumb="Prices"
        title="Transparent Pricing"
        subtitle="Clear hourly rates for each van and crew size. What’s included depends on the package — see each card below."
      />

      <section className="section">
        <div className="container-xl">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {VAN_PRICING.map((p) => (
              <div key={p.size} className={`price-card ${p.featured ? 'featured' : ''}`}>
                {p.featured && <div className="inline-block mb-3 px-3 py-1 bg-[color:var(--mv-orange)] text-white rounded-full text-xs font-bold tracking-wider uppercase">Most Popular</div>}
                <div className="text-slate-500 font-semibold">{p.size}</div>
                <div className="flex items-end gap-1 mt-2">
                  <span className="font-display text-5xl font-extrabold text-[color:var(--mv-navy)]">{p.price}</span>
                  <span className="text-slate-500 pb-2">{p.unit.startsWith('per ') ? '/ ' + p.unit.slice(4) : p.unit}</span>
                </div>
                <div className="text-sm text-slate-500 mt-1">{p.capacity} · {p.best}</div>
                <ul className="mt-4 space-y-2">
                  {p.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-slate-700">
                      <CheckCircle2 className="w-4 h-4 text-[color:var(--mv-orange)]" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/#quote" className={`mt-5 w-full justify-center ${p.featured ? 'btn-primary' : 'btn-ghost-dark'}`}>Book this van</Link>
              </div>
            ))}
          </div>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { title: 'Minimum hours', text: 'Hourly bookings have a 2-hour minimum (4 hours for the 7.5 tonne lorry). Extra time beyond your booked slot is charged at 1.5× the hourly rate.' },
              { title: 'Travel outside London', text: 'Jobs outside our base London area include a mileage charge (40p a mile, or £1.75 a mile for the 7.5 tonne lorry). Your instant quote shows it up front.' },
              { title: 'Fixed-price moves', text: 'Longer-distance moves can be quoted at a fixed price agreed before the day.' },
            ].map((b) => (
              <div key={b.title} className="rounded-xl bg-[color:var(--mv-sky)] p-6 border border-slate-100">
                <div className="font-display text-xl font-extrabold text-[color:var(--mv-navy)]">{b.title}</div>
                <p className="text-slate-600 mt-2">{b.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section bg-[color:var(--mv-sky)]">
        <div className="container-xl grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div>
            <div className="text-[color:var(--mv-orange)] font-bold tracking-[0.2em] text-xs uppercase mb-3">FAQ</div>
            <h2 className="font-display text-4xl font-extrabold text-[color:var(--mv-navy)] leading-tight">Frequently asked questions</h2>
            <p className="text-slate-600 mt-4">Can’t find what you’re looking for? Our friendly team is just a call away.</p>
            <a href={`tel:${COMPANY.phone.replace(/\s/g, '')}`} className="btn-primary mt-6"><Phone className="w-4 h-4" /> {COMPANY.phone}</a>
          </div>
          <div className="lg:col-span-2">
            <Accordion type="single" collapsible className="bg-white rounded-xl border border-slate-100 divide-y">
              {FAQS.map((f, i) => (
                <AccordionItem value={`item-${i}`} key={f.q} className="px-5">
                  <AccordionTrigger className="text-left font-display font-bold text-[color:var(--mv-navy)] text-lg hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-xl">
          <div className="rounded-2xl bg-[color:var(--mv-navy)] text-white p-12 flex flex-col lg:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-display text-3xl font-extrabold">Not sure which van you need?</h3>
              <p className="text-white/70 mt-2">We’ll help you pick the right size — and we’ll never oversell.</p>
            </div>
            <Link to="/#quote" className="btn-primary">Get a free quote <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Prices;
